import type { Brand } from '|/brand';
import { isMobile } from '|/helpers';
import {
  PersonaClient,
  PersonaClientInquirySelector,
  PersonaStatic,
  ResumableInquiry
} from '../types';

declare global {
  interface Window {
    Persona: PersonaStatic;
  }
}

const getPersonaProductionConfig = () => {
  return {
    environment: 'production',
    environment_id: 'env_RaVqJ19bkNAVzjXieqoKLCcH',
    templateIds: {
      'co-sign-flow': 'itmpl_sdFXRKQshckRwvMReq1p1iszmqaz',
      'primary-flow': 'itmpl_9tXrF22DzSbtMxypMX41bwm8TKq8',
      'mobile-co-sign-flow': 'itmpl_Eb9PK8kMZ6BPXX8BRTASjhZm8QJK'
    },
    slateThemeSetId: 'theset_ACY6MWZHhR8ejQ3PTJNkXgkt7s1iRi'
  };
};

const getPersonaTestConfig = () => {
  return {
    environment: 'sandbox',
    environment_id: 'env_aQ8W2gQPhHEcDL9kNPR8nF4i',
    templateIds: {
      'co-sign-flow': 'itmpl_sdFXRKQshckRwvMReq1p1iszmqaz',
      'primary-flow': 'itmpl_9tXrF22DzSbtMxypMX41bwm8TKq8',
      'mobile-co-sign-flow': 'itmpl_Eb9PK8kMZ6BPXX8BRTASjhZm8QJK'
    },
    slateThemeSetId: 'theset_ACY6MWZHhR8ejQ3PTJNkXgkt7s1iRi'
  };
};

export interface PersonaOptions {
  onCancel: (response: ResumableInquiry, templateId: string) => void;
  onComplete: (inquiryId: string, templateId: string) => void;
  onLoad: (timeElapsed: number, templateId: string) => void;
  onError: (templateId: string) => void;
  onReady: (timeElapsed: number, templateId: string) => void;
  onEvent: () => void;
}

export const createPersonaClient = async (
  isCobuyer: boolean,
  sendEvent: (name: string, success: string, metadata: unknown) => void,
  options: PersonaOptions,
  userId: string,
  brand: Brand,
  resumableInquiry?: ResumableInquiry | null
): Promise<PersonaClient | null> => {
  const personaConfiguration =
    process.env.NODE_ENV === 'production' ? getPersonaProductionConfig() : getPersonaTestConfig();

  const inquiryId = resumableInquiry?.inquiryId;
  const sessionToken = resumableInquiry?.sessionToken;

  const templateId = isCobuyer
    ? isMobile()
      ? personaConfiguration.templateIds['mobile-co-sign-flow']
      : personaConfiguration.templateIds['co-sign-flow']
    : personaConfiguration.templateIds['primary-flow'];

  const themeSetId = brand === 'slate' ? personaConfiguration.slateThemeSetId : undefined;

  // Persona SDK v4.12.1 (public/index.html) — sessionToken is the live resume-token config key;
  // the SDK renamed it from accessToken in 4.0.0, and the shipped bundle reads only the new name.
  // Sending both this pair and templateId together throws, so PersonaClientInquirySelector models
  // them as mutually exclusive rather than as independent optionals. referenceId rides with
  // templateId: a resumed inquiry keeps the account it was created under.
  const inquirySelector: PersonaClientInquirySelector =
    inquiryId && sessionToken
      ? { inquiryId, sessionToken }
      : { templateId, referenceId: isCobuyer ? `${userId}-Cosigner` : userId };

  const startTimer = performance.now();

  try {
    return await new window.Persona.Client({
      environment: personaConfiguration.environment,
      environment_id: personaConfiguration.environment_id,
      onCancel: metadata => {
        options.onCancel(metadata, templateId);
      },
      onComplete: (metadata: { inquiryId: string }) => {
        options.onComplete(metadata.inquiryId, templateId);
      },
      onLoad: () => {
        const timeElapsed = performance.now() - startTimer;
        options.onLoad(timeElapsed, templateId);
      },
      onError: () => {
        options.onError(templateId);
      },
      onReady: () => {
        const timeElapsed = performance.now() - startTimer;
        options.onReady(timeElapsed, templateId);
      },
      onEvent: (eventName, metadata) => {
        if (
          eventName !== 'complete' &&
          eventName !== 'start' &&
          eventName !== 'ready' &&
          eventName !== 'load'
        ) {
          sendEvent('pp_persona_event', 'True', {
            isCosigner: isCobuyer,
            eventName,
            details: metadata,
            templateId
          });
        }
      },
      ...inquirySelector,
      ...(themeSetId ? { themeSetId } : {})
    });
  } catch (error) {
    const timeElapsed = performance.now() - startTimer;
    sendEvent('pp_persona_create_failure', 'False', {
      isCosigner: isCobuyer,
      timeElapsed,
      error
    });
  }

  return null;
};
